import { useCallback, useState } from "react";
import toast from "react-hot-toast";
import {
  MAX_ATTACHMENTS, fileToAttachment, looksLikeCode, pastedCodeAttachment,
  resolveAttachment, urlAttachment,
} from "@/pages/multi-agent/ide/attachments";
import { isWhitespace } from "@/services/multi-agent";
import type { MultiAgentAttachment } from "@/services/multi-agent/api";

export interface UseAttachmentsReturn {
  attachments: MultiAgentAttachment[];
  dragOver: boolean;
  attachmentsBusy: boolean;
  sendableAttachments: boolean;
  setDragOver: (v: boolean) => void;
  updateAttachment: (id: string, patch: Partial<MultiAgentAttachment>) => void;
  removeAttachment: (id: string) => void;
  addAndResolve: (att: MultiAgentAttachment) => Promise<void>;
  addFiles: (files: FileList | File[]) => Promise<void>;
  onComposerPaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => Promise<void>;
  onComposerDrop: (e: React.DragEvent) => void;
  promoteTypedUrls: (input: string) => void;
  clearAttachments: () => void;
  setInputFromTranscript: (append: (prev: string) => string) => void;
}

/**
 * Manages multimodal attachment state: images, PDFs, audio, URLs, code.
 * `onAppendToInput` is called when an audio attachment is transcribed — the
 * caller owns the composer text state and applies the transcript there.
 *
 * `onInsertCodeFence` is the SAME kind of handoff for a short pasted-code detection
 * (`looksLikeCode`, below `onComposerPaste`): the hook decides WHETHER a paste should become a
 * fenced block, the caller decides WHERE — at the composer's actual cursor position, which needs
 * the textarea's DOM node and is the route's to own, not this hook's.
 */
export function useAttachments(
  onAppendToInput: (text: string) => void,
  onInsertCodeFence: (raw: string) => void,
): UseAttachmentsReturn {
  const [attachments, setAttachments] = useState<MultiAgentAttachment[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const updateAttachment = (id: string, patch: Partial<MultiAgentAttachment>) =>
    setAttachments((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));

  const removeAttachment = (id: string) =>
    setAttachments((prev) => prev.filter((a) => a.id !== id));

  // Stable: the route subscribes it to "new conversation" and the lane-v2 composer keys an effect on it.
  const clearAttachments = useCallback(() => setAttachments([]), []);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const setInputFromTranscript = (_fn: (prev: string) => string) => {
    // Intentional no-op slot — caller provides onAppendToInput.
  };

  const addAndResolve = async (att: MultiAgentAttachment) => {
    let blocked = false;
    setAttachments((prev) => {
      if (prev.length >= MAX_ATTACHMENTS) { blocked = true; return prev; }
      return [...prev, att];
    });
    if (blocked) { toast.error(`Up to ${MAX_ATTACHMENTS} attachments`); return; }
    if (att.status === "ready" && att.markdown) return;
    const resolved = await resolveAttachment(att);
    if (att.kind === "audio" && resolved.markdown) {
      onAppendToInput(resolved.markdown);
      updateAttachment(att.id, { status: "ready", markdown: undefined });
    } else {
      updateAttachment(att.id, {
        markdown: resolved.markdown,
        status: resolved.status,
        errorReason: resolved.errorReason,
      });
      if (resolved.status === "error") {
        toast.error(`${resolved.name}: ${resolved.errorReason || "could not be processed"}`);
      }
    }
  };

  const addFiles = async (files: FileList | File[]) => {
    const slice = Array.from(files).slice(0, MAX_ATTACHMENTS);
    await Promise.all(
      slice.map(async (f) => {
        const r = await fileToAttachment(f);
        if ("error" in r) { toast.error(r.error); return; }
        await addAndResolve(r);
      }),
    );
  };

  const findUrlsInText = (text: string, existing: Set<string>): string[] => {
    const out: string[] = [];
    const tokens: string[] = [];
    let cur = "";
    for (const ch of text) {
      if (isWhitespace(ch)) {
        if (cur) { tokens.push(cur); cur = ""; }
      } else {
        cur += ch;
      }
    }
    if (cur) tokens.push(cur);
    for (const rawTok of tokens) {
      let tok = rawTok;
      while (tok.length && (tok.endsWith(".") || tok.endsWith(",") || tok.endsWith(";") ||
        tok.endsWith(":") || tok.endsWith(")") || tok.endsWith("]") || tok.endsWith('"') || tok.endsWith("'"))) {
        tok = tok.slice(0, -1);
      }
      const prefix = tok.startsWith("https://") ? 8 : tok.startsWith("http://") ? 7 : -1;
      if (prefix > 0 && tok.length > prefix && !existing.has(tok) && !out.includes(tok)) {
        out.push(tok);
      }
    }
    return out;
  };

  const onComposerPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const imageFiles: File[] = [];
    for (const it of Array.from(e.clipboardData?.items || [])) {
      if (it.type.startsWith("image/")) {
        const file = it.getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length) { e.preventDefault(); await addFiles(imageFiles); return; }

    const text = e.clipboardData?.getData("text") || "";
    const trimmed = text.trim();
    const urlPrefix = trimmed.startsWith("https://") ? 8 : trimmed.startsWith("http://") ? 7 : -1;
    const isBareUrl =
      urlPrefix > 0 &&
      trimmed.length > urlPrefix &&
      ![...trimmed.slice(urlPrefix)].some(isWhitespace);
    if (isBareUrl) {
      e.preventDefault(); void addAndResolve(urlAttachment(trimmed)); return;
    }
    if (text.length > 1500 && text.includes("\n")) {
      e.preventDefault(); void addAndResolve(pastedCodeAttachment(text)); return;
    }
    /*
     * THE GAP THIS CLOSES. A paste at or under the 1500-char attachment threshold fell through to
     * here and did nothing — the browser's own default paste behaviour then inserted it as
     * literal, unformatted text, which is exactly the reported bug for a short multi-line snippet
     * copied from an editor. `looksLikeCode` is conservative on purpose (see its own comment); a
     * paste it does not recognise as code still falls through to the browser's default, unchanged
     * from today. One that IS recognised gets fenced and handed to the caller to insert AT THE
     * CURSOR (not appended), so typing text before or after the paste in the same message works.
     */
    if (text.includes("\n") && looksLikeCode(text)) {
      e.preventDefault();
      onInsertCodeFence(text);
      return;
    }
  };

  const onComposerDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) void addFiles(e.dataTransfer.files);
  };

  const promoteTypedUrls = (input: string) => {
    const existing = new Set<string>();
    for (const a of attachments) {
      if (a.kind === "url" && a.content) existing.add(a.content);
    }
    const found = findUrlsInText(input, existing);
    let room = MAX_ATTACHMENTS - attachments.length;
    for (const url of found) {
      if (room <= 0) break;
      room -= 1;
      void addAndResolve(urlAttachment(url));
    }
  };

  const attachmentsBusy = attachments.some((a) => a.status === "processing");
  const sendableAttachments = attachments.some((a) => a.markdown && a.markdown.trim());

  return {
    attachments, dragOver, attachmentsBusy, sendableAttachments,
    setDragOver, updateAttachment, removeAttachment, addAndResolve,
    addFiles, onComposerPaste, onComposerDrop, promoteTypedUrls,
    clearAttachments, setInputFromTranscript,
  };
}
